/**
 * EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Company Integrations Page
 * 
 * Consolidates all tenant-level integrations into a single page:
 * - Google Vision OCR (full management)
 * - Google Maps API (read-only, platform-managed)
 * 
 * This replaces the Integrations tab from the old CompanySettingsPage.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import api, { extractErrorMessage } from "../lib/api";
import { usePlatformSettings } from "../components/settings/PlatformSettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Plug,
  Cloud,
  ScanEye,
  MapPin,
  Shield,
  Key,
  Upload,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink,
  HelpCircle,
  Zap,
  Trash2,
  Loader2,
  FileJson,
  Info,
  ChevronRight,
  Settings,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_COMPANY_ID = "default";

// Help links for Google Cloud setup
const HELP_LINKS = {
  enableVisionApi: "https://console.cloud.google.com/apis/library/vision.googleapis.com",
  createServiceAccount: "https://cloud.google.com/iam/docs/creating-managing-service-accounts",
  generateJsonKey: "https://cloud.google.com/iam/docs/creating-managing-service-account-keys",
  fixOrgPolicy: "https://cloud.google.com/resource-manager/docs/organization-policy/overview",
  enableRoutesApi: "https://console.cloud.google.com/apis/library/routes.googleapis.com",
  createApiKey: "https://cloud.google.com/docs/authentication/api-keys",
};

// ============== STATUS BADGE COMPONENT ==============
function StatusBadge({ status, testResult }) {
  if (!status || status === "not_configured") {
    return (
      <Badge variant="outline" className="gap-1 text-muted-foreground" data-testid="status-not-configured">
        <XCircle className="w-3 h-3" />
        Not Configured
      </Badge>
    );
  }

  if (testResult === "success") {
    return (
      <Badge variant="default" className="gap-1 bg-emerald-600" data-testid="status-success">
        <CheckCircle2 className="w-3 h-3" />
        Working
      </Badge>
    );
  }

  if (testResult === "error") {
    return (
      <Badge variant="destructive" className="gap-1" data-testid="status-error">
        <AlertTriangle className="w-3 h-3" />
        Error
      </Badge>
    );
  }

  return (
    <Badge variant="secondary" className="gap-1" data-testid="status-configured">
      <CheckCircle2 className="w-3 h-3" />
      Configured
    </Badge>
  );
}

// ============== HELP LINK COMPONENT ==============
function HelpLink({ href, children }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-primary transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
          </a>
        </TooltipTrigger>
        <TooltipContent>{children}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ============== MAIN COMPONENT ==============
export default function CompanyIntegrationsPage() {
  // Platform settings for Google Maps status
  const { settings: platformSettings, loading: platformLoading } = usePlatformSettings();
  
  // Integration states
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [visionStatus, setVisionStatus] = useState(null);
  
  // Vision OCR states
  const fileInputRef = useRef(null);
  const [uploadingVision, setUploadingVision] = useState(false);
  const [testingVision, setTestingVision] = useState(false);
  
  // Help dialog
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  // Fetch integration status
  const fetchIntegrationStatus = useCallback(async () => {
    try {
      // EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Use existing backend endpoint
      const response = await api.get(`/integrations/google/status?company_id=${DEFAULT_COMPANY_ID}`);
      setVisionStatus(response.data.vision || { status: "not_configured" });
    } catch (error) {
      console.error("Failed to fetch integration status:", error);
      setVisionStatus({ status: "not_configured" });
    } finally {
      setIntegrationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntegrationStatus();
  }, [fetchIntegrationStatus]);

  // Vision OCR handlers
  const handleVisionFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".json")) {
      toast.error("Please select a JSON file");
      return;
    }

    setUploadingVision(true);
    try {
      const text = await file.text();
      const credentials = JSON.parse(text);
      
      // Validate required fields
      if (!credentials.type || !credentials.project_id || !credentials.private_key) {
        toast.error("Invalid service account JSON. Missing required fields.");
        return;
      }

      // EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Use existing backend endpoint
      await api.post(`/integrations/google/vision/credentials`, {
        credentials_json: credentials,
        company_id: DEFAULT_COMPANY_ID,
      });

      toast.success("Vision credentials uploaded successfully");
      fetchIntegrationStatus();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON file");
      } else {
        toast.error("Failed to upload credentials: " + extractErrorMessage(error));
      }
    } finally {
      setUploadingVision(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleTestVision = async () => {
    setTestingVision(true);
    try {
      // EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Use existing backend endpoint
      const response = await api.post(`/integrations/google/test-vision?company_id=${DEFAULT_COMPANY_ID}`);
      if (response.data.success) {
        toast.success("Vision API connection successful!");
      } else {
        toast.error("Vision API test failed: " + (response.data.error || "Unknown error"));
      }
      fetchIntegrationStatus();
    } catch (error) {
      toast.error("Vision API test failed: " + extractErrorMessage(error));
    } finally {
      setTestingVision(false);
    }
  };

  const handleDeleteVision = async () => {
    if (!confirm("Are you sure you want to delete the Vision credentials?")) return;

    try {
      // EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Use existing backend endpoint
      await api.delete(`/integrations/google/vision/credentials?company_id=${DEFAULT_COMPANY_ID}`);
      toast.success("Vision credentials deleted");
      fetchIntegrationStatus();
    } catch (error) {
      toast.error("Failed to delete credentials: " + extractErrorMessage(error));
    }
  };

  // Google Maps status from platform settings
  const googleMapsEnabled = platformSettings?.google_maps_api_enabled || false;

  return (
    <div className="space-y-6" data-testid="company-integrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <a href="/settings" className="hover:text-foreground">Settings</a>
            <ChevronRight className="h-4 w-4" />
            <a href="/settings" className="hover:text-foreground">Company</a>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">Integrations</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Plug className="h-6 w-6 text-primary" />
            Integrations
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Connect external services to enhance document processing and logistics
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowHelpDialog(true)}
          data-testid="google-setup-help-btn"
        >
          <HelpCircle className="w-4 h-4 mr-2" />
          Setup Guide
        </Button>
      </div>

      {integrationsLoading || platformLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Google Vision OCR Section */}
          <Card data-testid="vision-ocr-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <ScanEye className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Google Vision OCR</CardTitle>
                    <CardDescription>
                      Extract text from identity documents using Google Cloud Vision API
                    </CardDescription>
                  </div>
                </div>
                <StatusBadge
                  status={visionStatus?.status}
                  testResult={visionStatus?.last_test_result}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Configuration */}
              {visionStatus?.configured && (
                <div className="p-4 rounded-lg bg-surface-elevated border border-border/50">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-emerald-500" />
                        <span className="font-medium">Credentials Configured</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Project: <span className="font-mono">{visionStatus.project_id}</span>
                      </p>
                      {visionStatus.client_email && (
                        <p className="text-sm text-muted-foreground">
                          Service Account: <span className="font-mono text-xs">{visionStatus.client_email}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleTestVision}
                        disabled={testingVision}
                        data-testid="test-vision-btn"
                      >
                        {testingVision ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Test Connection
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDeleteVision}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        data-testid="delete-vision-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {visionStatus.last_test_timestamp && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Last tested: {new Date(visionStatus.last_test_timestamp).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {/* Upload Credentials */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="flex items-center gap-2">
                    <FileJson className="w-4 h-4 text-muted-foreground" />
                    Service Account Credentials
                  </Label>
                  <HelpLink href={HELP_LINKS.createServiceAccount}>
                    How to create a service account
                  </HelpLink>
                </div>
                
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleVisionFileSelect}
                    className="hidden"
                    data-testid="vision-file-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingVision}
                    className="flex-1"
                    data-testid="upload-vision-btn"
                  >
                    {uploadingVision ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {visionStatus?.configured ? "Replace Credentials" : "Upload JSON Credentials"}
                  </Button>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Upload your Google Cloud service account JSON key file. 
                  Credentials are stored securely and never exposed after upload.
                </p>
              </div>

              <Separator />

              {/* Help Links */}
              <div className="flex flex-wrap gap-3 text-sm">
                <a
                  href={HELP_LINKS.enableVisionApi}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  Enable Vision API <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href={HELP_LINKS.generateJsonKey}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  Generate JSON Key <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href={HELP_LINKS.fixOrgPolicy}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  Fix Organization Policy <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Google Maps Section (Platform-Managed, Read-Only) */}
          <Card data-testid="google-maps-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <MapPin className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      Google Maps API
                      <Badge variant="outline" className="text-xs font-normal">
                        Platform Managed
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Calculate travel distances and durations for crew assignments
                    </CardDescription>
                  </div>
                </div>
                {googleMapsEnabled ? (
                  <Badge variant="default" className="gap-1 bg-emerald-600" data-testid="maps-status-enabled">
                    <CheckCircle2 className="w-3 h-3" />
                    Enabled
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1 text-muted-foreground" data-testid="maps-status-disabled">
                    <XCircle className="w-3 h-3" />
                    Disabled
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Platform-Level Integration</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    The Google Maps API is managed at the platform level and shared across all tenants.
                    This ensures consistent travel calculations and centralized billing.
                  </p>
                  <div className="pt-2">
                    <a
                      href="/settings/platform"
                      className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
                    >
                      <Settings className="w-4 h-4" />
                      Go to Platform Settings to configure
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Future Integrations Placeholder */}
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-8 text-center">
              <Cloud className="w-10 h-10 text-muted-foreground mb-3" />
              <h3 className="font-medium text-muted-foreground">More Integrations Coming Soon</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Payment processing, email services, and more
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Help Dialog */}
      <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5 text-primary" />
              Google Cloud Integration Setup
            </DialogTitle>
            <DialogDescription>
              Follow these steps to configure Google Cloud integrations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Vision API Setup */}
            <div className="space-y-3">
              <h3 className="font-medium flex items-center gap-2">
                <ScanEye className="w-4 h-4 text-primary" />
                Vision OCR Setup
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>
                  <a href={HELP_LINKS.enableVisionApi} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Enable the Vision API
                  </a> in your Google Cloud Console
                </li>
                <li>
                  <a href={HELP_LINKS.createServiceAccount} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Create a service account
                  </a> with Vision API access
                </li>
                <li>
                  <a href={HELP_LINKS.generateJsonKey} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Download the JSON key file
                  </a> for your service account
                </li>
                <li>Upload the JSON file using the upload button above</li>
                <li>Test the connection to verify it works</li>
              </ol>
            </div>

            <Separator />

            {/* Maps API Info */}
            <div className="space-y-3">
              <h3 className="font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" />
                Google Maps API
              </h3>
              <p className="text-sm text-muted-foreground">
                The Google Maps API is configured at the platform level by your system administrator.
                Contact your admin if you need travel distance calculations enabled.
              </p>
            </div>

            <Separator />

            {/* Common Issues */}
            <div className="space-y-3">
              <h3 className="font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Common Issues
              </h3>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                <li>
                  <strong>Permission denied:</strong> Ensure the service account has the correct IAM roles
                </li>
                <li>
                  <strong>API not enabled:</strong> Check that the Vision API is enabled in your project
                </li>
                <li>
                  <strong>Organization policy:</strong> Some organizations restrict service account key creation
                </li>
                <li>
                  <strong>Billing:</strong> Ensure billing is enabled on your Google Cloud project
                </li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
